import { BotPromptChannel, BotPromptInterface, BotPromptSection, ExpectedPromptRecord, StarterConfig } from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

export interface ResolvedTextResult {
  text: string;
  source: "default" | "parent" | "org";
  sourceOrgId?: number;
  sourceOrgName?: string;
  /** Tool-specific: resolved enabled message */
  enabledMessage?: string;
  /** Tool-specific: resolved disabled message */
  disabledMessage?: string;
  /** Starters only: how the resolved layer opens (mode + verbatim messages). */
  starterConfig?: StarterConfig;
}

// LINK apps/server/src/API/Bot/BotPrompt.Controller.ts
export class BotPromptsAPI {
  static async getPrompts(orgId: number): Promise<BotPromptInterface[]> {
    return APIService.Server.get<BotPromptInterface[]>(`/bot-prompts/org/${orgId}`);
  }

  static async getPrompt(orgId: number, id: number): Promise<BotPromptInterface> {
    return APIService.Server.get<BotPromptInterface>(`/bot-prompts/org/${orgId}/prompt/${id}`);
  }

  /** Get all records (drafts, published, archived) for a given slug+channel — the version list. */
  static async getRecords(orgId: number, slug: string, channel?: BotPromptChannel): Promise<BotPromptInterface[]> {
    const params: Record<string, any> = { slug };
    if (channel) params.channel = channel;
    return APIService.Server.get<BotPromptInterface[]>(`/bot-prompts/org/${orgId}/records`, params);
  }

  static async createPrompt(orgId: number, data: Partial<BotPromptInterface>): Promise<BotPromptInterface> {
    return APIService.Server.post<BotPromptInterface>(`/bot-prompts/org/${orgId}`, data);
  }

  static async updatePrompt(orgId: number, id: number, data: Partial<BotPromptInterface>): Promise<BotPromptInterface> {
    return APIService.Server.put<BotPromptInterface>(`/bot-prompts/org/${orgId}/prompt/${id}`, data);
  }

  static async deletePrompt(orgId: number, id: number): Promise<void> {
    return APIService.Server.delete(`/bot-prompts/org/${orgId}/prompt/${id}`);
  }

  static async unpublishPrompt(orgId: number, id: number): Promise<BotPromptInterface> {
    return APIService.Server.post<BotPromptInterface>(`/bot-prompts/org/${orgId}/prompt/${id}/unpublish`);
  }

  static async publishPrompt(orgId: number, id: number): Promise<BotPromptInterface> {
    return APIService.Server.post<BotPromptInterface>(`/bot-prompts/org/${orgId}/prompt/${id}/publish`);
  }

  /** Copy any record into a new draft. */
  static async copyToDraft(orgId: number, id: number, label?: string): Promise<BotPromptInterface> {
    return APIService.Server.post<BotPromptInterface>(`/bot-prompts/org/${orgId}/prompt/${id}/copy-to-draft`, { label });
  }

  /** Return the list of default prompt records the org is missing. */
  static async getMissingDefaults(orgId: number): Promise<ExpectedPromptRecord[]> {
    return APIService.Server.get<ExpectedPromptRecord[]>(`/bot-prompts/org/${orgId}/missing-defaults`);
  }

  /** Copy missing defaults (or a subset) into the org as published records. */
  static async copyFromDefaults(orgId: number, records?: { slug: string; channel?: BotPromptChannel }[]): Promise<BotPromptInterface[]> {
    return APIService.Server.post<BotPromptInterface[]>(`/bot-prompts/org/${orgId}/copy-from-defaults`, { records });
  }

  static async getResolvedSections(orgId: number, slug: string, channel?: BotPromptChannel): Promise<BotPromptSection[]> {
    const params: Record<string, any> = { slug };
    if (channel) params.channel = channel;
    return APIService.Server.get<BotPromptSection[]>(`/bot-prompts/org/${orgId}/resolved-prompt`, params);
  }

  /**
   * Resolve the plain-text value for a slug (greetings, sequences, tool descriptions)
   * along with which layer it came from so the editor can show an inherited preview.
   */
  static async getResolvedText(orgId: number, slug: string, channel?: BotPromptChannel): Promise<ResolvedTextResult> {
    const params: Record<string, any> = { slug };
    if (channel) params.channel = channel;
    return APIService.Server.get<ResolvedTextResult>(`/bot-prompts/org/${orgId}/resolved-text`, params);
  }
}
